import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const LINES = [
  [
    { t: "From Creative Vision to " },
    { t: "AI-Optimized", em: true },
    { t: " Commercial." },
  ],
  [{ t: "Broadcast Ready." }],
  [
    { t: "Made by " },
    { t: "Real Film Directors", em: true },
    { t: "." },
  ],
  [
    { t: "A Million-Dollar Production, Without the " },
    { t: "Million-Dollar Headache", em: true },
    { t: "." },
  ],
  [{ t: "Commercials in Days, Not Months." }],
  [
    { t: "AI Does the Rendering. " },
    { t: "Directors Do the Directing", em: true },
    { t: "." },
  ],
];

const TYPE_MS = 85;
const HOLD_MS = 2400;
const DELETE_MS = 24;

function TypedLine({ segments, charCount }) {
  let remaining = charCount;
  return (
    <span className="inline">
      {segments.map((seg, i) => {
        const visible = seg.t.slice(0, Math.max(0, Math.min(remaining, seg.t.length)));
        remaining -= seg.t.length;
        return seg.em ? (
          <em key={i} className="text-[#E1A730]">
            {visible}
          </em>
        ) : (
          <span key={i}>{visible}</span>
        );
      })}
      <span className="type-caret" data-testid="hero-tagline-caret" aria-hidden="true" />
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const [started, setStarted] = useState(false);
  const [lineIndex, setLineIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [phase, setPhase] = useState("typing");

  const totalChars = LINES[lineIndex].reduce((sum, seg) => sum + seg.t.length, 0);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), 1100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!started) return;
    let t;
    if (phase === "typing") {
      if (charCount < totalChars) {
        t = setTimeout(() => setCharCount((c) => c + 1), TYPE_MS);
      } else {
        setPhase("holding");
      }
    } else if (phase === "holding") {
      t = setTimeout(() => setPhase("deleting"), HOLD_MS);
    } else if (phase === "deleting") {
      if (charCount > 0) {
        t = setTimeout(() => setCharCount((c) => c - 1), DELETE_MS);
      } else {
        setLineIndex((i) => (i + 1) % LINES.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [started, phase, charCount, totalChars]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-[100svh] flex-col items-center justify-center overflow-hidden px-6"
    >
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="flex w-full flex-col items-center text-center"
      >
        <div className="relative">
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.65, 0.25, 0] }}
            transition={{ duration: 3, delay: 0.3, times: [0, 0.35, 0.7, 1], ease: "easeOut" }}
            className="absolute -inset-24 rounded-full bg-white/15 blur-3xl"
          />
          <motion.img
            src="/assets/bite-logo.png"
            alt="Bite logotype — AI advertising and full production studio for the Middle East"
            data-testid="hero-logo"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.95, 0.15, 1, 0.45, 1],
              scale: [1.05, 1],
              filter: [
                "blur(18px) brightness(3.5)",
                "blur(6px) brightness(1.6)",
                "blur(0px) brightness(1)",
              ],
            }}
            transition={{
              opacity: { duration: 1.8, times: [0, 0.13, 0.24, 0.42, 0.54, 1], ease: "easeOut" },
              scale: { duration: 2.1, ease: EASE },
              filter: { duration: 1.9, times: [0, 0.5, 1], ease: "easeOut" },
            }}
            className="relative w-[min(68vw,520px)]"
          />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          data-testid="hero-overline"
          className="mt-10 font-mono text-[10px] tracking-[0.45em] text-zinc-500 md:text-xs"
        >
          AI ADVERTISING · FULL PRODUCTION STUDIO
        </motion.p>

        <h1
          data-testid="hero-tagline"
          className="relative mt-6 w-full font-serif text-3xl font-normal leading-[1.3] tracking-tight sm:text-4xl lg:text-5xl"
        >
          <span className="sr-only">
            Bite — AI advertising and commercial production studio serving Cairo, Riyadh, Dubai,
            Doha and Kuwait
          </span>
          <span className="invisible block" aria-hidden="true">
            {LINES[3].map((seg, i) => (
              <em key={i}>{seg.t}</em>
            ))}
          </span>
          {started && (
            <span
              data-testid="hero-tagline-line"
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="block">
                <TypedLine segments={LINES[lineIndex]} charCount={charCount} />
              </span>
            </span>
          )}
        </h1>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        data-testid="scroll-indicator"
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] tracking-[0.4em] text-zinc-500">SCROLL</span>
        <div className="h-12 w-px overflow-hidden bg-zinc-800">
          <motion.div
            className="h-4 w-px bg-white"
            animate={{ y: [-16, 48] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
